import React, { useState, useEffect, useRef, useCallback } from "react";
import { useBlocker } from "react-router-dom";
import {
  Plus,
  X,
  AlertCircle,
  Loader2,
  Code,
  Trash2,
  Check,
  Save,
} from "lucide-react";
import {
  GetAxios,
  PostAxios,
  PatchAxios,
  DeleteAxios,
} from "../../helpers/axiosHelpers";
import { toast } from "sonner";
import RichTextEditor from "./RichTextEditor";
import useAuthStore from "../../store/authStore";
import { canCreate, canEdit, canDelete } from "../../utils/permissions";

// ─── Chips / Tag Input ────────────────────────────────────────────────────────
const ChipsInput = ({ value = [], onChange, placeholder }) => {
  const [inputVal, setInputVal] = useState("");

  const chips = Array.isArray(value) ? value : [];

  const addChip = () => {
    const trimmed = inputVal.trim().replace(/,+$/, "");
    if (trimmed && !chips.includes(trimmed)) {
      onChange([...chips, trimmed]);
    }
    setInputVal("");
  };

  const removeChip = (chip) => onChange(chips.filter((c) => c !== chip));

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addChip();
    } else if (e.key === "Backspace" && !inputVal && chips.length > 0) {
      removeChip(chips[chips.length - 1]);
    }
  };

  return (
    <div className="w-full min-h-[52px] bg-white border border-slate-200 rounded-2xl px-4 py-2.5 flex flex-wrap gap-2 items-center focus-within:ring-4 focus-within:ring-subidha-blue/10 focus-within:border-subidha-blue transition-all">
      {chips.map((chip) => (
        <span
          key={chip}
          className="flex items-center gap-1 bg-subidha-blue/10 text-subidha-blue text-[11px] font-black px-2.5 py-1 rounded-lg"
        >
          {`{{${chip}}}`}
          <button
            type="button"
            onClick={() => removeChip(chip)}
            className="hover:text-red-500 transition-colors ml-0.5"
          >
            <X size={11} />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={addChip}
        placeholder={
          chips.length === 0 ? placeholder || "Type variable, press Enter…" : ""
        }
        className="flex-1 min-w-[140px] outline-none text-sm font-bold text-slate-700 bg-transparent"
      />
    </div>
  );
};

/**
 * Reusable CRUD Manager (page layout)
 *
 * Renders as an inline admin page (not a modal overlay) with a single Save
 * action. Navigation away while the form has unsaved changes is guarded by a
 * modal (Save & continue / Keep editing / Leave without saving).
 */
const CrudManager = ({
  title,
  apiEndpoint,
  fields,
  renderItem,
  icon: Icon,
  emptyMessage = "No items yet. Create your first one!",
  formatCreateData,
  formatUpdateData,
}) => {
  const user = useAuthStore((s) => s.user);
  const userCanCreate = canCreate(user);
  const userCanEdit = canEdit(user);
  const userCanDelete = canDelete(user);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  const [showGuard, setShowGuard] = useState(false);
  const [guardBusy, setGuardBusy] = useState(false);
  const formRef = useRef(null);
  const initialFormData = useRef({});
  const pendingLeave = useRef(null);

  const isFormOpen = showAddForm || !!editingItem;

  // Helper variables for Email Templates (can be moved to props if needed)
  const templateVariables = [
    "{{candidateName}}",
    "{{position}}",
    "{{interviewDate}}",
    "{{location}}",
  ];

  const getFields = () => {
    let currentFields =
      typeof fields === "function" ? fields(!!editingItem) : fields;
    if (editingItem) {
      currentFields = currentFields.filter((field) => !field.hideOnEdit);
    } else {
      currentFields = currentFields.filter((field) => !field.hideOnCreate);
    }
    return currentFields;
  };

  useEffect(() => {
    fetchItems();
  }, [apiEndpoint]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await GetAxios(apiEndpoint);
      if (response.success) setItems(response.data);
    } catch (err) {
      setError("Failed to sync with server");
    } finally {
      setLoading(false);
    }
  };

  // ─── Navigation guard (unsaved changes) ───────────────────────────────────
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        isFormOpen &&
        formDirty &&
        currentLocation.pathname !== nextLocation.pathname &&
        // Allow explicit logout / auth-redirect to /login to proceed (session
        // is being torn down anyway; otherwise ProtectedRoute would loop).
        nextLocation.pathname !== "/login",
      [isFormOpen, formDirty],
    ),
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      pendingLeave.current = () => blocker.proceed();
      setShowGuard(true);
    }
  }, [blocker.state]);

  // Warn before leaving / refreshing the tab with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (isFormOpen && formDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isFormOpen, formDirty]);

  const openGuard = (afterLeave) => {
    pendingLeave.current = afterLeave;
    setShowGuard(true);
  };

  const guardKeepEditing = () => {
    setShowGuard(false);
    pendingLeave.current = null;
    if (blocker.state === "blocked") blocker.reset();
  };

  const guardLeave = () => {
    setShowGuard(false);
    const leave = pendingLeave.current;
    pendingLeave.current = null;
    closeForm();
    leave?.();
  };

  const guardSaveAndContinue = async () => {
    setGuardBusy(true);
    const ok = await handleAction();
    setGuardBusy(false);
    if (ok) {
      setShowGuard(false);
      const leave = pendingLeave.current;
      pendingLeave.current = null;
      leave?.();
    }
  };

  // ─── Form actions ─────────────────────────────────────────────────────────
  const handleInputChange = (fieldName, value) => {
    setFormDirty(true);
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleStartAdd = () => {
    const doAdd = () => {
      setShowAddForm(true);
      setEditingItem(null);
      setFormData({});
      setFormDirty(false);
      setError(null);
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    };
    if (isFormOpen && formDirty) openGuard(doAdd);
    else doAdd();
  };

  const startEdit = (item) => {
    const doEdit = () => {
      const allFields = typeof fields === "function" ? fields(false) : fields;
      const editData = {};
      allFields.forEach((field) => {
        if (field.hideOnEdit) return;
        if (field.valueExtractor) {
          editData[field.name] = field.valueExtractor(item);
        } else if (field.type === "chips") {
          editData[field.name] = Array.isArray(item[field.name])
            ? item[field.name]
            : [];
        } else {
          editData[field.name] = item[field.name] ?? "";
        }
      });
      setFormData(editData);
      setEditingItem(item);
      setShowAddForm(false);
      setFormDirty(false);
      setError(null);
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    };
    if (isFormOpen && formDirty) openGuard(doEdit);
    else doEdit();
  };

  const closeForm = () => {
    setEditingItem(null);
    setShowAddForm(false);
    setFormData({});
    setFormDirty(false);
    setError(null);
  };

  // Returns true on success so the guard can continue navigation after saving
  const handleAction = async () => {
    const visibleFields = getFields();
    const missing = visibleFields.filter((f) => {
      if (!f.required) return false;
      const val = formData[f.name];
      if (f.type === "chips") return !Array.isArray(val) || val.length === 0;
      return !val?.toString().trim();
    });

    if (missing.length > 0) {
      setError(
        `Missing required fields: ${missing.map((f) => f.label).join(", ")}`,
      );
      return false;
    }

    try {
      setSubmitting(true);
      setError(null);

      const allFields = typeof fields === "function" ? fields(false) : fields;
      const readonlyTypes = ["chips-readonly", "readonly"];

      const dataToSend = editingItem
        ? formatUpdateData
          ? formatUpdateData(formData)
          : Object.fromEntries(
              Object.entries(formData).filter(([key]) => {
                const f = allFields.find((f) => f.name === key);
                return !f || !readonlyTypes.includes(f.type);
              }),
            )
        : formatCreateData
          ? formatCreateData(formData)
          : Object.fromEntries(
              Object.entries(formData).filter(([key]) => {
                const f = allFields.find((f) => f.name === key);
                return !f || !readonlyTypes.includes(f.type);
              }),
            );

      const response = editingItem
        ? await PatchAxios(`${apiEndpoint}/${editingItem.id}`, dataToSend)
        : await PostAxios(apiEndpoint, dataToSend);

      if (response.success) {
        toast.success(
          editingItem ? "Changes saved successfully" : "Created successfully",
        );
        await fetchItems();
        closeForm();
        return true;
      }
      return false;
    } catch (err) {
      setError(err.response?.data?.msg || "Operation failed");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const baseClass =
      "w-full bg-white border border-slate-200 px-5 py-4 rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-subidha-blue/10 focus:border-subidha-blue transition-all";

    if (field.type === "textarea") {
      return (
        <div className="space-y-2">
          <textarea
            value={formData[field.name] || ""}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            rows={field.rows || 4}
            className={`${baseClass} resize-none font-mono text-xs`}
          />
          {field.name === "body" && (
            <div className="flex flex-wrap gap-1.5 p-3 bg-slate-50 rounded-xl border border-slate-100">
              <div className="w-full text-[9px] font-black text-slate-400 uppercase mb-1 flex items-center">
                <Code size={12} className="mr-1 text-subidha-blue" /> Quick
                Insert Variables:
              </div>
              {templateVariables.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() =>
                    handleInputChange(
                      field.name,
                      (formData[field.name] || "") + v,
                    )
                  }
                  className="text-[10px] font-mono bg-white border border-slate-200 text-slate-600 px-2 py-1 rounded-md hover:border-subidha-blue hover:text-subidha-blue transition-all"
                >
                  {v}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (field.type === "select") {
      return (
        <select
          value={formData[field.name] || ""}
          onChange={(e) => handleInputChange(field.name, e.target.value)}
          className={baseClass}
        >
          <option value="">Select {field.label}</option>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "richtext") {
      return (
        <div className="space-y-2">
          <RichTextEditor
            value={formData[field.name] || ""}
            onChange={(html) => handleInputChange(field.name, html)}
            placeholder={field.placeholder}
          />
        </div>
      );
    }

    if (field.type === "chips-readonly") {
      const chips = Array.isArray(formData[field.name])
        ? formData[field.name]
        : [];
      return (
        <div className="w-full min-h-[44px] bg-slate-50 border border-slate-200 rounded-2xl px-4 py-2.5 flex flex-wrap gap-2 items-center">
          {chips.length === 0 ? (
            <span className="text-[11px] text-slate-400 font-bold italic">
              Variables are auto-extracted from the email body
            </span>
          ) : (
            chips.map((chip) => (
              <span
                key={chip}
                className="flex items-center gap-1 bg-subidha-blue/10 text-subidha-blue text-[11px] font-black px-2.5 py-1 rounded-lg font-mono"
              >
                {`{{${chip}}}`}
              </span>
            ))
          )}
        </div>
      );
    }

    if (field.type === "chips") {
      return (
        <div className="space-y-1.5">
          <ChipsInput
            value={formData[field.name] || []}
            onChange={(val) => handleInputChange(field.name, val)}
            placeholder={field.placeholder}
          />
          <p className="text-[10px] text-slate-400 font-bold px-1">
            Type a variable name and press{" "}
            <kbd className="bg-slate-100 px-1 rounded">Enter</kbd> to add. These
            become{" "}
            <code className="text-subidha-blue">{`{{variableName}}`}</code>{" "}
            placeholders.
          </p>
        </div>
      );
    }

    if (field.type === "custom" && field.renderCustom) {
      return field.renderCustom(
        formData[field.name] ?? "",
        (val) => handleInputChange(field.name, val),
      );
    }

    return (
      <input
        type={field.type || "text"}
        value={formData[field.name] || ""}
        onChange={(e) => handleInputChange(field.name, e.target.value)}
        placeholder={field.placeholder}
        className={baseClass}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-[2.5rem] shadow-xl border border-slate-100 p-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-subidha-blue text-white rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              {Icon ? <Icon size={24} /> : <Plus size={24} />}
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-black text-slate-900">
                {title}
              </h2>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-0.5">
                Management Portal
              </p>
            </div>
          </div>
          {!isFormOpen && userCanCreate && (
            <button
              onClick={handleStartAdd}
              className="px-6 py-3 bg-subidha-blue text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-all flex items-center space-x-2 shrink-0"
            >
              <Plus size={18} />
              <span>Add New</span>
            </button>
          )}
        </div>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-6">
          Stored Records:{" "}
          <span className="text-slate-900 ml-1">{items.length}</span>
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-100 p-4 rounded-2xl flex items-center space-x-3 text-red-600 font-bold text-sm">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Form Card */}
      {isFormOpen && (
        <div
          ref={formRef}
          className="bg-white rounded-[2.5rem] shadow-xl border border-slate-100 p-8"
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-black text-slate-900">
              {editingItem ? "Update Entry" : "New Entry"}
            </h2>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Save to apply changes
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {getFields().map((field) => (
              <div
                key={field.name}
                className={field.fullWidth ? "md:col-span-2" : ""}
              >
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 mb-2 block">
                  {field.label} {field.required && "*"}
                </label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Single Save action — no cancel/discard button */}
          <div className="flex mt-8">
            <button
              onClick={handleAction}
              disabled={submitting}
              className="flex-1 px-6 py-4 rounded-2xl font-black text-white bg-subidha-blue hover:bg-slate-800 transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50 uppercase text-xs tracking-widest flex items-center justify-center space-x-2"
            >
              {submitting ? (
                <Loader2 className="animate-spin" />
              ) : (
                <>
                  <Save size={18} />
                  <span>
                    {editingItem ? "Save Changes" : "Save Entry"}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Add CTA when list view */}
      {!isFormOpen && userCanCreate && (
        <button
          onClick={handleStartAdd}
          className="w-full group p-6 bg-white border-2 border-dashed border-slate-200 rounded-[2rem] flex items-center justify-center space-x-3 hover:border-subidha-blue hover:bg-subidha-blue/[0.02] transition-all"
        >
          <Plus className="text-slate-400 group-hover:text-subidha-blue" />
          <span className="font-black text-slate-400 group-hover:text-subidha-blue uppercase tracking-widest text-xs">
            Add New Record
          </span>
        </button>
      )}

      {/* Records List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex flex-col items-center py-20 opacity-20">
            <Loader2 className="animate-spin" size={40} />
          </div>
        ) : items.length > 0 ? (
          items.map((item) => (
            <div
              key={item.id}
              className="p-1 bg-white border border-slate-100 rounded-[1.5rem] hover:shadow-xl hover:shadow-slate-200/50 transition-all"
            >
              <div className="p-4">
                {renderItem(
                  item,
                  userCanEdit ? startEdit : undefined,
                  userCanDelete ? (it) => setDeletingItem(it) : undefined,
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-20 text-slate-400 font-bold uppercase tracking-widest text-[10px]">
            {emptyMessage}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingItem && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[70] flex items-center justify-center p-6">
          <div className="bg-white rounded-[2.5rem] p-10 max-w-sm w-full text-center space-y-6 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto">
              <Trash2 size={40} />
            </div>
            <div>
              <h3 className="text-2xl font-black text-slate-900">
                Are you sure?
              </h3>
              <p className="text-slate-500 font-medium mt-2">
                This record will be permanently purged from the system.
              </p>
            </div>
            <div className="flex flex-col space-y-3 pt-2">
              <button
                onClick={async () => {
                  try {
                    await DeleteAxios(`${apiEndpoint}/${deletingItem.id}`);
                    toast.success("Record deleted");
                    fetchItems();
                    setDeletingItem(null);
                  } catch (e) {
                    toast.error("Delete failed");
                  }
                }}
                className="w-full py-4 bg-red-600 text-white rounded-2xl font-black hover:bg-red-700 transition-all uppercase text-xs tracking-widest"
              >
                Delete Now
              </button>
              <button
                onClick={() => setDeletingItem(null)}
                className="w-full py-4 bg-slate-100 text-slate-500 rounded-2xl font-black hover:bg-slate-200 transition-all uppercase text-xs tracking-widest"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Guard Modal */}
      {showGuard && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[80] flex items-center justify-center p-6">
          <div className="bg-white rounded-[2.5rem] p-10 max-w-md w-full text-center space-y-6 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="w-20 h-20 bg-subidha-blue/10 text-subidha-blue rounded-full flex items-center justify-center mx-auto">
              <AlertCircle size={40} />
            </div>
            <div>
              <h3 className="text-2xl font-black text-slate-900">
                Unsaved Changes
              </h3>
              <p className="text-slate-500 font-medium mt-2">
                You have unsaved changes in the form. What would you like to do?
              </p>
            </div>
            <div className="flex flex-col space-y-3 pt-2">
              <button
                onClick={guardSaveAndContinue}
                disabled={guardBusy}
                className="w-full py-4 bg-subidha-blue text-white rounded-2xl font-black hover:bg-slate-800 transition-all disabled:opacity-50 uppercase text-xs tracking-widest flex items-center justify-center space-x-2"
              >
                {guardBusy ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <>
                    <Check size={16} />
                    <span>Save &amp; Continue</span>
                  </>
                )}
              </button>
              <button
                onClick={guardKeepEditing}
                className="w-full py-4 bg-slate-100 text-slate-500 rounded-2xl font-black hover:bg-slate-200 transition-all uppercase text-xs tracking-widest"
              >
                Keep Editing
              </button>
              <button
                onClick={guardLeave}
                className="w-full py-4 bg-red-50 text-red-600 rounded-2xl font-black hover:bg-red-100 transition-all uppercase text-xs tracking-widest"
              >
                Leave without Saving
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrudManager;
